// src/components/chat/RoutingExpander.jsx
/**
 * RoutingExpander — "Why this model?" toggle rendered alongside an assistant
 * message header. Reveals worker-side reasoning (benchmark, complexity tier,
 * confidence, ideal model that was blocked, etc.).
 *
 * Reads from `message.routing`, which the backend attaches when model="auto"
 * was used. If the user picked a model explicitly, `routing` is undefined and
 * this renders nothing. (The old WebSearchBadge died with the third-party
 * search-engine path — web search is native to the serving provider now and
 * emits no routing.web_search block.)
 */

import React, { useState } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import styles from './RoutingExpander.module.css';


export const RoutingExpander = ({ routing }) => {
    const [open, setOpen] = useState(false);
    if (!routing) return null;

    const fields = [];
    if (routing.benchmark)        fields.push(['Benchmark',     routing.benchmark]);
    if (routing.complexity_tier)  fields.push(['Complexity',    routing.complexity_tier]);
    if (typeof routing.confidence === 'number')
                                  fields.push(['Confidence',    `${(routing.confidence * 100).toFixed(0)}%`]);
    if (routing.ideal_model)      fields.push(['Unrestricted ideal', routing.ideal_model]);
    if (routing.blocked_by)       fields.push(['Blocked by',    routing.blocked_by]);
    if (routing.vision_promoted)  fields.push(['Vision promoted', 'yes']);
    if (routing.tool_promoted)    fields.push(['Tools promoted', 'yes']);
    if (routing.promoted_from)    fields.push(['Promoted from', routing.promoted_from]);
    if (routing.ema_active)       fields.push(['EMA active',    'yes']);

    if (!fields.length) return null;

    return (
        <div className={styles.expanderWrap}>
            <button
                type="button"
                className={styles.expanderToggle}
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
            >
                <Info size={11} />
                <span>Why this model?</span>
                <ChevronDown
                    size={11}
                    className={`${styles.chev} ${open ? styles.chevOpen : ''}`}
                />
            </button>
            {open && (
                <dl className={styles.kvList}>
                    {fields.map(([k, v]) => (
                        <div key={k} className={styles.kvRow}>
                            <dt className={styles.kvKey}>{k}</dt>
                            <dd className={styles.kvVal}>{v}</dd>
                        </div>
                    ))}
                    {routing.warnings?.length > 0 && (
                        <div className={`${styles.kvRow} ${styles.warnings}`}>
                            <dt className={styles.kvKey}>Warnings</dt>
                            <dd className={styles.kvVal}>
                                {routing.warnings.map((w, i) => (
                                    <div key={i}>{w}</div>
                                ))}
                            </dd>
                        </div>
                    )}
                </dl>
            )}
        </div>
    );
};
